import { useState, useEffect, useRef } from 'react';
import Button from '../base/Button';
import Input from '../base/Input';

declare global {
  interface Window {
    grecaptcha: {
      render: (container: string | HTMLElement, parameters: object) => number;
      getResponse: (widgetId?: number) => string;
      reset: (widgetId?: number) => void;
    };
    onRecaptchaLoad: () => void;
  }
}

const INTEREST_OPTIONS = [
  { value: 'Full home security system installation', icon: 'ri-home-4-line' },
  { value: 'Security cameras only', icon: 'ri-camera-line' },
  { value: 'Smart doorbell and locks', icon: 'ri-door-lock-line' },
  { value: 'Not sure — I need a free assessment', icon: 'ri-question-line' },
];

export default function SecurityAuditForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [zipCode, setZipCode] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [interest, setInterest] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const recaptchaRef = useRef<HTMLDivElement>(null);
  const recaptchaWidgetId = useRef<number | null>(null);

  const validateEmail = (emailVal: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailVal);
  };

  const validatePhone = (phone: string) => {
    const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
    return phoneRegex.test(phone);
  };

  const validateZip = (zip: string) => {
    return /^\d{5}$/.test(zip.trim());
  };

  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    const limitedDigits = digits.slice(0, 10);
    if (limitedDigits.length >= 6) {
      return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
    } else if (limitedDigits.length >= 3) {
      return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
    } else if (limitedDigits.length > 0) {
      return `(${limitedDigits}`;
    }
    return '';
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
  };

  // Step 1: Zip Code
  const handleStep1Submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!zipCode.trim()) {
      setSubmitMessage('Please enter your zip code');
      return;
    }
    if (!validateZip(zipCode)) {
      setSubmitMessage('Please enter a valid 5-digit zip code');
      return;
    }
    setCurrentStep(2);
    setSubmitMessage('');
  };

  // Step 2: Name, Phone, Email
  const handleStep2Submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim()) {
      setSubmitMessage('Please enter your full name');
      return;
    }
    if (!phoneNumber.trim()) {
      setSubmitMessage('Please enter your phone number');
      return;
    }
    if (!validatePhone(phoneNumber)) {
      setSubmitMessage('Please enter a valid phone number');
      return;
    }
    if (!email.trim()) {
      setSubmitMessage('Please enter your email address');
      return;
    }
    if (!validateEmail(email)) {
      setSubmitMessage('Please enter a valid email address');
      return;
    }
    setCurrentStep(3);
    setSubmitMessage('');
  };

  // Step 3: Interest + reCAPTCHA
  useEffect(() => {
    if (currentStep === 3) {
      const renderWidget = () => {
        if (
          recaptchaRef.current &&
          window.grecaptcha &&
          recaptchaWidgetId.current === null
        ) {
          recaptchaWidgetId.current = window.grecaptcha.render(recaptchaRef.current, {
            sitekey: '6Ldon4wsAAAAACmkIlrzxPgAOfxjVKiVDCJu7S9N',
            callback: () => setRecaptchaVerified(true),
            'expired-callback': () => setRecaptchaVerified(false),
          });
        }
      };

      if (window.grecaptcha) {
        renderWidget();
      } else {
        const interval = setInterval(() => {
          if (window.grecaptcha) {
            renderWidget();
            clearInterval(interval);
          }
        }, 300);
        return () => clearInterval(interval);
      }
    }
  }, [currentStep]);

  const handleStep3Submit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!interest) {
      setSubmitMessage('Please select what you are most interested in');
      return;
    }

    if (!recaptchaVerified) {
      setSubmitMessage('Please complete the reCAPTCHA verification');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await fetch('https://homsecurityadvisors.com/success.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          zip_code: zipCode.trim(),
          full_name: fullName.trim(),
          email: email.trim(),
          phone: phoneNumber.trim(),
          interest: interest.trim(),
        }),
      });

      if (response.ok) {
        setCurrentStep(4);
        setSubmitMessage('Thank you! We will contact you shortly to schedule your free security audit.');
      } else {
        setSubmitMessage('There was an error submitting your request. Please try again.');
        if (recaptchaWidgetId.current !== null) {
          window.grecaptcha.reset(recaptchaWidgetId.current);
          setRecaptchaVerified(false);
        }
      }
    } catch (error) {
      setSubmitMessage('There was an error submitting your request. Please try again.');
      if (recaptchaWidgetId.current !== null) {
        window.grecaptcha.reset(recaptchaWidgetId.current);
        setRecaptchaVerified(false);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getProgressWidth = () => {
    switch (currentStep) {
      case 1: return 'w-1/3';
      case 2: return 'w-2/3';
      case 3: return 'w-full';
      case 4: return 'w-full';
      default: return 'w-1/3';
    }
  };

  // ── STEP 1: Zip Code ──────────────────────────────────────────────────────
  const renderStep1 = () => (
    <form onSubmit={handleStep1Submit}>
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-map-pin-2-line text-orange-500 text-2xl"></i>
        </div>
        <h4 className="text-xl font-bold text-gray-900 mb-2">Enter Zip Code to Check Availability</h4>
        <p className="text-gray-600">See if our security experts are available in your area</p>
      </div>

      <div className="mb-6">
        <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-2">
          Zip Code
        </label>
        <Input
          type="text"
          name="zip_code"
          id="zipCode"
          placeholder="e.g. 89101"
          value={zipCode}
          onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
          required
          maxLength={5}
        />
        <p className="text-xs text-gray-500 mt-1">Enter your 5-digit US zip code</p>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-green-800">
          <span className="font-semibold">We serve all 50 states!</span> Enter your zip code to confirm local expert availability.
        </p>
      </div>

      <Button
        type="submit"
        className="w-full flex items-center justify-center space-x-2"
      >
        <span>Check Availability</span>
        <i className="ri-arrow-right-line"></i>
      </Button>
    </form>
  );

  // ── STEP 2: Name, Phone, Email ────────────────────────────────────────────
  const renderStep2 = () => (
    <form onSubmit={handleStep2Submit}>
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-user-line text-orange-500 text-2xl"></i>
        </div>
        <h4 className="text-xl font-bold text-gray-900 mb-2">Your Contact Information</h4>
        <p className="text-gray-600">We&apos;ll use this to reach you about your free audit</p>
      </div>

      <div className="space-y-4 mb-6">
        <div>
          <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
            Full Name
          </label>
          <Input
            type="text"
            name="full_name"
            id="fullName"
            placeholder="Enter your full name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
          />
        </div>

        <div>
          <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number
          </label>
          <Input
            type="tel"
            name="phone"
            id="phoneNumber"
            placeholder="(123) 456-7890"
            value={phoneNumber}
            onChange={handlePhoneChange}
            required
          />
          <p className="text-xs text-gray-500 mt-1">Format: (XXX) XXX-XXXX</p>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <Input
            type="email"
            name="email"
            id="email"
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
      </div>

      <div className="flex space-x-3">
        <Button
          type="button"
          onClick={() => setCurrentStep(1)}
          className="flex-1 bg-gray-500 hover:bg-gray-600 flex items-center justify-center space-x-2"
        >
          <i className="ri-arrow-left-line"></i>
          <span>Back</span>
        </Button>
        <Button
          type="submit"
          className="flex-1 flex items-center justify-center space-x-2"
        >
          <span>Continue</span>
          <i className="ri-arrow-right-line"></i>
        </Button>
      </div>
    </form>
  );

  // ── STEP 3: Interest + reCAPTCHA ─────────────────────────────────────────
  const renderStep3 = () => (
    <form onSubmit={handleStep3Submit}>
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-shield-check-line text-orange-500 text-2xl"></i>
        </div>
        <h4 className="text-xl font-bold text-gray-900 mb-2">What are you most interested in?</h4>
        <p className="text-gray-600">Select the option that best fits your needs</p>
      </div>

      <div className="space-y-3 mb-6">
        {INTEREST_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setInterest(option.value)}
            className={`w-full flex items-center space-x-4 px-4 py-4 rounded-lg border-2 transition-all duration-200 cursor-pointer text-left whitespace-normal ${
              interest === option.value
                ? 'border-orange-500 bg-orange-50'
                : 'border-gray-200 bg-white hover:border-orange-300 hover:bg-orange-50/40'
            }`}
          >
            <div className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 ${
              interest === option.value ? 'bg-orange-500' : 'bg-gray-100'
            }`}>
              <i className={`${option.icon} text-lg ${interest === option.value ? 'text-white' : 'text-gray-500'}`}></i>
            </div>
            <span className={`font-medium text-sm leading-snug ${
              interest === option.value ? 'text-orange-700' : 'text-gray-700'
            }`}>
              {option.value}
            </span>
            {interest === option.value && (
              <div className="ml-auto flex-shrink-0 w-5 h-5 flex items-center justify-center">
                <i className="ri-check-line text-orange-500 text-base"></i>
              </div>
            )}
          </button>
        ))}
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Security Verification
        </label>
        <div ref={recaptchaRef}></div>
        {!recaptchaVerified && submitMessage === 'Please complete the reCAPTCHA verification' && (
          <p className="text-xs text-red-500 mt-1">Please check the box above to verify you&apos;re human</p>
        )}
      </div>

      <div className="flex space-x-3">
        <Button
          type="button"
          onClick={() => setCurrentStep(2)}
          className="flex-1 bg-gray-500 hover:bg-gray-600 flex items-center justify-center space-x-2"
        >
          <i className="ri-arrow-left-line"></i>
          <span>Back</span>
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          className="flex-1 flex items-center justify-center space-x-2"
        >
          <span>{isSubmitting ? 'Submitting...' : 'Continue & See What It Costs to Protect My Home'}</span>
          <i className="ri-check-line"></i>
        </Button>
      </div>
    </form>
  );

  // ── STEP 4: Success ───────────────────────────────────────────────────────
  const renderStep4 = () => (
    <div className="text-center">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <i className="ri-check-line text-green-500 text-2xl"></i>
      </div>
      <h4 className="text-xl font-bold text-gray-900 mb-2">Request Submitted Successfully!</h4>
      <p className="text-gray-600 mb-6">
        Thank you for your interest in our free security audit. Our expert will contact you within 24 hours to schedule your appointment.
      </p>

      <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
        <h5 className="font-semibold text-gray-900 mb-3">What happens next?</h5>
        <div className="space-y-2 text-sm text-gray-700">
          <div className="flex items-center space-x-2">
            <i className="ri-phone-line text-green-500"></i>
            <span>Our security expert will call you to schedule your audit</span>
          </div>
          <div className="flex items-center space-x-2">
            <i className="ri-calendar-line text-green-500"></i>
            <span>We&apos;ll arrange a convenient time for your free assessment</span>
          </div>
          <div className="flex items-center space-x-2">
            <i className="ri-file-list-3-line text-green-500"></i>
            <span>You&apos;ll receive a detailed security report with recommendations</span>
          </div>
        </div>
      </div>

      <div className="text-center">
        <p className="text-sm text-gray-600 mb-4">Need immediate assistance?</p>
        <button className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-medium flex items-center space-x-2 mx-auto whitespace-nowrap cursor-pointer">
          <i className="ri-phone-line"></i>
          <span>Call Now for Instant Quote: (702) 760-1474</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-2xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900">Free Security Audit</h3>
          <span className="text-sm text-gray-500">{currentStep < 4 ? `Step ${currentStep} of 3` : 'Complete!'}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div className={`bg-orange-500 h-2 rounded-full transition-all duration-300 ${getProgressWidth()}`}></div>
        </div>
      </div>

      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}
      {currentStep === 3 && renderStep3()}
      {currentStep === 4 && renderStep4()}

      {/* Urgency badge — shown on all active steps */}
      {currentStep < 4 && (
        <div className="flex items-center justify-center space-x-2 bg-amber-50 border border-amber-300 rounded-lg px-4 py-2 mt-4">
          <span className="text-base">⚠️</span>
          <p className="text-sm font-semibold text-amber-800">Only 7 installation slots available this week in your area</p>
        </div>
      )}

      {submitMessage && currentStep < 4 && (
        <div className={`mt-4 p-3 rounded-lg text-sm ${
          submitMessage.includes('Thank you')
            ? 'bg-green-50 text-green-800 border border-green-200'
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {submitMessage}
        </div>
      )}
    </div>
  );
}
